/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { FirebaseService } from 'src/shared/firebase/firebase.service';
import { NotificationService } from 'src/shared/notification.service';
import { globalWindow } from 'src/shared/window-globals';
import { NoteCompDataTypes } from './shared/note-comp-data-types.enum';
import { DocumentLibraryFile } from './shared/text-messaging/text-message';
import { WorkspaceService } from './workspace.service';

@Injectable({
  providedIn: 'root',
})
export class WorkspaceComponentRefService implements OnDestroy {
  readonly #firebaseService = inject(FirebaseService);
  readonly #ngZone = inject(NgZone);
  readonly #notificationService = inject(NotificationService);
  readonly #workspaceService = inject(WorkspaceService);

  setWorkspaceComponentRef(): void {
    globalWindow.workspaceComponentRef = {
      component: this,
      editTask: this.#editTask,
      openNote: this.#openNote,
      addToDiscussion: this.#addToDiscussion,
      populateNote: this.#populateNote,
      openText: this.#openText,
      editOpportunity: this.#editOpportunity,
      opportunitySaved: this.#opportunitySaved,
      noteAdded: this.#noteAdded,
      taskAdded: this.#taskAdded,
      taskUpdated: this.#taskUpdated,
      discussionUpdated: this.#discussionUpdated,
      firebase: {
        getUser: this.#firebaseService.getUser,
        watchUserNode: this.#firebaseService.watchUserNode,
        saveUserNode: this.#firebaseService.saveUserNode,
        watchNode: this.#firebaseService.watchNode,
        saveNode: this.#firebaseService.saveNode,
        watchSystemMessageNode: this.#firebaseService.watchSystemMessageNode,
        watchBulkImportNode: this.#firebaseService.watchBulkImportNode,
        watchPoliciesNode: this.#firebaseService.watchPoliciesNode,
        removePoliciesNode: this.#firebaseService.removePoliciesNode,
        watchAccountNode: this.#firebaseService.watchAccountNode,
        removeAccountNode: this.#firebaseService.removeAccountNode,
      },
      notify: this.#notificationService.notify,
      retentionCenterNoteAdded: this.#retentionCenterNoteAdded,
      clearOutRetentionApplicants: this.#clearOutRetentionApplicants,
    };
  }

  ngOnDestroy(): void {
    globalWindow.workspaceComponentRef = null;
  }

  #noteAdded = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.noteAdded.subscribe(func);
    });
  };

  #taskAdded = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.taskAdded.subscribe(func);
    });
  };

  #taskUpdated = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.taskUpdated.subscribe(func);
    });
  };

  #discussionUpdated = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.discussionUpdated.subscribe(func);
    });
  };

  #editTask = (task: { taskId: number }): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.selectTask.emit(task.taskId);
    });
  };

  #openNote = (): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.openNote();
    });
  };

  #addToDiscussion = (note: { discussionId: number }): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.openNote({ discussionId: note.discussionId, type: NoteCompDataTypes.Select });
    });
  };

  #populateNote = (note: any, opportunities: any, onNoteSaved: (discussionId?: number) => void): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.openNote({ note, opportunities, onNoteSaved, type: NoteCompDataTypes.Populate });
    });
  };

  #openText = (applicantId: number, phoneNumber: string, doc: DocumentLibraryFile, message?: string): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.openTextMessaging(applicantId, phoneNumber, message, doc);
    });
  };

  #editOpportunity = (opportunity: any, status?: any): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.openNote({ opportunity, status, type: NoteCompDataTypes.Opportunity });
    });
  };

  #opportunitySaved = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.opportunitySaved.subscribe(func);
    });
  };

  #retentionCenterNoteAdded = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.retentionCenterNoteAdded.subscribe(func);
    });
  };

  #clearOutRetentionApplicants = (func: Function): void => {
    this.#ngZone.run(() => {
      this.#workspaceService.workspaceState.clearOutRetentionApplicants.subscribe(func);
    });
  };
}
